import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Slide,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { createCorrectionRequest } from "../../../api/TimeTracking/timeTrackingApi";
import {
  CLOCK_EVENT_OPTIONS,
  PAUSE_TYPE_OPTIONS,
} from "./clockEventLabels";
import { ttFilterFieldSx, ttSectionTitleSx } from "./timeTrackingStyles";

const WORK_MODES = [
  { value: "presencial", label: "Presencial", short: "Oficina" },
  { value: "teletrabajo", label: "Teletrabajo", short: "Remoto" },
  { value: "hibrido", label: "Híbrido", short: "Híbrido" },
];

function toDatetimeLocalValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function recordedAtFromLocal(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function ClockTimeCard({
  theme,
  label,
  hint,
  icon: Icon,
  accent,
  value,
  onChange,
}) {
  const filled = Boolean(value);
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: filled ? alpha(accent, 0.45) : alpha(theme.palette.divider, 0.7),
        bgcolor: filled
          ? alpha(accent, 0.07)
          : alpha(theme.palette.background.paper, 0.85),
        boxShadow: filled
          ? `0 4px 16px ${alpha(accent, 0.12)}`
          : "none",
        transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(accent, filled ? 0.18 : 0.1),
            color: accent,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {hint}
          </Typography>
        </Box>
      </Stack>
      <TextField
        type="datetime-local"
        size="small"
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="dd/mm/aaaa --:--"
        InputLabelProps={{ shrink: true }}
        inputProps={{ "aria-label": label }}
        sx={{
          ...ttFilterFieldSx(theme),
          "& .MuiInputBase-root": {
            ...ttFilterFieldSx(theme)["& .MuiInputBase-root"],
            fontWeight: filled ? 700 : 500,
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <ScheduleOutlinedIcon sx={{ fontSize: 18, color: accent, opacity: 0.85 }} />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}

const CorrectionRequestDialog = ({
  open,
  mode,
  entry,
  onClose,
  onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const accent = theme.palette.secondary.main;
  const isCorrect = mode === "correct";
  const [eventType, setEventType] = useState("clock_in");
  const [pauseType, setPauseType] = useState("comida");
  const [workMode, setWorkMode] = useState("presencial");
  const [recordedAt, setRecordedAt] = useState("");
  const [clockInAt, setClockInAt] = useState("");
  const [clockOutAt, setClockOutAt] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReason("");
    if (isCorrect && entry) {
      setEventType(entry.eventType);
      setPauseType(entry.pauseType || "comida");
      setWorkMode(entry.workMode || "presencial");
      setRecordedAt(toDatetimeLocalValue(entry.recordedAt));
      setClockInAt("");
      setClockOutAt("");
    } else {
      setEventType("clock_in");
      setPauseType("comida");
      setWorkMode("presencial");
      setRecordedAt(toDatetimeLocalValue(new Date().toISOString()));
      setClockInAt("");
      setClockOutAt("");
    }
  }, [open, isCorrect, entry]);

  const handleSave = async () => {
    if (!reason.trim()) {
      setError("Indica el motivo (obligatorio para trazabilidad)");
      return;
    }

    if (isCorrect) {
      const iso = recordedAtFromLocal(recordedAt);
      if (!iso) {
        setError("Indica fecha y hora válidas");
        return;
      }
      if (!entry?.id) {
        setError("Fichaje no válido");
        return;
      }
      if (eventType === "pause_start" && !pauseType) {
        setError("Indica el tipo de pausa");
        return;
      }

      try {
        setSaving(true);
        setError(null);
        await createCorrectionRequest({
          requestType: "correct_entry",
          targetEntryId: entry.id,
          eventType,
          workMode,
          recordedAt: iso,
          reason: reason.trim(),
          notes: eventType === "pause_start" ? pauseType : undefined,
        });
        onSaved?.();
        onClose();
      } catch (e) {
        setError(e?.response?.data?.error || "No se pudo enviar la solicitud");
      } finally {
        setSaving(false);
      }
      return;
    }

    const clockInIso = recordedAtFromLocal(clockInAt);
    const clockOutIso = recordedAtFromLocal(clockOutAt);
    if (clockInAt && !clockInIso) {
      setError("Indica una fecha y hora de entrada válidas");
      return;
    }
    if (clockOutAt && !clockOutIso) {
      setError("Indica una fecha y hora de salida válidas");
      return;
    }
    if (!clockInIso && !clockOutIso) {
      setError("Indica al menos la entrada o la salida");
      return;
    }
    if (clockInIso && clockOutIso && new Date(clockOutIso) <= new Date(clockInIso)) {
      setError("La salida debe ser posterior a la entrada");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await createCorrectionRequest({
        requestType: "add_missing",
        workMode,
        reason: reason.trim(),
        clockInAt: clockInIso || null,
        clockOutAt: clockOutIso || null,
      });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo enviar la solicitud");
    } finally {
      setSaving(false);
    }
  };

  const title = isCorrect
    ? "Solicitar corrección"
    : "Fichaje olvidado";
  const subtitle = isCorrect
    ? "Corrige la hora de un fichaje existente"
    : "Indica entrada, salida o ambas";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.background.default, 0.98),
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2, sm: 2.25 },
          pb: 2,
          background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 72%)`,
          borderBottom: "1px solid",
          borderColor: alpha(accent, 0.16),
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(accent, 0.16),
              color: accent,
              flexShrink: 0,
            }}
          >
            <EditCalendarOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={saving}
            size="small"
            aria-label="Cerrar"
            sx={{
              mt: -0.25,
              bgcolor: alpha(theme.palette.text.primary, 0.04),
            }}
          >
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          pb: { xs: 1.5, sm: 2 },
        }}
      >
        <Stack spacing={2.25}>
          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
              p: 1.25,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: "1px solid",
              borderColor: alpha(theme.palette.info.main, 0.22),
            }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 18, color: theme.palette.info.main, mt: 0.15, flexShrink: 0 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {isCorrect
                ? "Si RRHH aprueba, se creará un registro nuevo. El fichaje original se conserva."
                : "La solicitud queda pendiente de RRHH. Puedes indicar solo entrada, solo salida o ambas."}
            </Typography>
          </Box>

          {isCorrect ? (
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.7),
                bgcolor: alpha(theme.palette.background.paper, 0.9),
              }}
            >
              <Typography sx={{ ...ttSectionTitleSx, mb: 1.5 }}>
                Datos del fichaje
              </Typography>
              <Stack spacing={1.5}>
                <FormControl size="small" fullWidth sx={ttFilterFieldSx(theme)}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={eventType}
                    label="Tipo"
                    onChange={(e) => setEventType(e.target.value)}
                  >
                    {CLOCK_EVENT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {eventType === "pause_start" ? (
                  <FormControl size="small" fullWidth sx={ttFilterFieldSx(theme)}>
                    <InputLabel>Tipo de pausa</InputLabel>
                    <Select
                      value={pauseType}
                      label="Tipo de pausa"
                      onChange={(e) => setPauseType(e.target.value)}
                    >
                      {PAUSE_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}

                <TextField
                  label="Fecha y hora correcta"
                  type="datetime-local"
                  size="small"
                  fullWidth
                  value={recordedAt}
                  onChange={(e) => setRecordedAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={ttFilterFieldSx(theme)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ScheduleOutlinedIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Box>
          ) : (
            <Box>
              <Typography sx={{ ...ttSectionTitleSx, mb: 1.25 }}>
                ¿Qué olvidaste fichar?
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mb: 0.75 }}
              >
                <ClockTimeCard
                  theme={theme}
                  label="Entrada"
                  hint="Opcional"
                  icon={LoginOutlinedIcon}
                  accent={theme.palette.success.main}
                  value={clockInAt}
                  onChange={setClockInAt}
                />
                <ClockTimeCard
                  theme={theme}
                  label="Salida"
                  hint="Opcional"
                  icon={LogoutOutlinedIcon}
                  accent={theme.palette.text.secondary}
                  value={clockOutAt}
                  onChange={setClockOutAt}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Al menos uno es obligatorio. Si olvidaste toda la jornada, rellena ambos.
              </Typography>
            </Box>
          )}

          <Box>
            <Typography sx={{ ...ttSectionTitleSx, mb: 1.25 }}>
              Modalidad
            </Typography>
            <ToggleButtonGroup
              value={workMode}
              exclusive
              fullWidth
              onChange={(_, value) => {
                if (value) setWorkMode(value);
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  flex: 1,
                  py: 1.1,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  borderColor: alpha(theme.palette.divider, 0.75),
                  "&.Mui-selected": {
                    bgcolor: alpha(accent, 0.14),
                    color: accent,
                    borderColor: alpha(accent, 0.4),
                    "&:hover": {
                      bgcolor: alpha(accent, 0.2),
                    },
                  },
                },
              }}
            >
              {WORK_MODES.map((m) => (
                <ToggleButton key={m.value} value={m.value}>
                  {isMobile ? m.short : m.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography sx={{ ...ttSectionTitleSx, mb: 1.25 }}>
              Motivo
            </Typography>
            <TextField
              size="small"
              fullWidth
              required
              multiline
              minRows={isMobile ? 3 : 2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explica qué ocurrió y por qué necesitas esta solicitud…"
              sx={ttFilterFieldSx(theme)}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          pt: 0,
          gap: 1,
          flexDirection: { xs: "column-reverse", sm: "row" },
          borderTop: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.55),
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          position: isMobile ? "sticky" : "static",
          bottom: 0,
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          fullWidth={isMobile}
          sx={{
            fontWeight: 700,
            borderRadius: 2,
            py: 1.1,
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          fullWidth={isMobile}
          startIcon={<SendOutlinedIcon />}
          sx={{
            fontWeight: 800,
            borderRadius: 2,
            py: 1.1,
            boxShadow: `0 6px 20px ${alpha(accent, 0.28)}`,
          }}
        >
          {saving ? "Enviando…" : "Enviar solicitud"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CorrectionRequestDialog;
